/**
 * Relative Volume (RVol) — a bar's volume as a PERCENT of the average volume
 * over the prior `avgLength` sessions (100 = at average, 150 = 50% above).
 * The baseline EXCLUDES the current bar so a spike isn't diluted by itself.
 *
 *   RVol_i = 100 × Volume_i / mean(Volume_{i-avgLength .. i-1})
 *
 * Capped at 9999 (>100×) → null: illiquid names with a near-zero baseline
 * otherwise explode to millions of percent and pollute sorts. Index-aligned to
 * `closes`/`volumes`; the scanner takes the last value (avgLength=50 default).
 */
import { familyGroup } from "./indicatorGroups";
import type { OHLCV, IndicatorParams, SharedIndicatorDef } from "./types";

const RVOL_CAP = 9999;

export const RELATIVE_VOLUME: SharedIndicatorDef = {
  id: "rvol",
  label: "Afiat - Relative Volume (RVol)",
  group: familyGroup("rvol"),
  format: "percent",
  referenceLevels: [100],
  params: [
    { id: "avgLength", label: "Avg volume length", kind: "number", default: 50, min: 1, max: 400, step: 1 },
  ],
  computeSeries(data: OHLCV, params: IndicatorParams): (number | null)[] {
    const { volumes } = data;
    const n = volumes.length;
    const out: (number | null)[] = new Array(n).fill(null);
    if (n < 2) return out;

    const avgLength = Math.max(1, Math.round(params.avgLength ?? 50));

    // Prefix sums for O(n) trailing averages. prefix[k] = sum(volumes[0..k-1]).
    const prefix: number[] = new Array(n + 1).fill(0);
    for (let i = 0; i < n; i++) prefix[i + 1] = prefix[i]! + volumes[i]!;

    for (let i = 1; i < n; i++) {
      const start = Math.max(0, i - avgLength);
      const count = i - start; // prior bars available (excludes bar i)
      if (count <= 0) continue;
      const avg = (prefix[i]! - prefix[start]!) / count;
      const cur = volumes[i]!;
      if (avg > 0 && Number.isFinite(cur)) {
        const r = (cur / avg) * 100;
        out[i] = r <= RVOL_CAP ? r : null;
      }
    }
    return out;
  },
};
